import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Property, Unit, UnitStatus } from '@/types/property.types';
import { propertiesService } from '@/services/properties.service';
import { Button } from '@/components/ui/Button';

export const PropertyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [property, setProperty] = useState<Property | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<string>('all');

  useEffect(() => {
    const fetchProperty = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        setError(null);
        const response = await propertiesService.getById(id);
        setProperty(response.data);
      } catch (err) {
        setError('Failed to load property details');
        console.error('Error fetching property:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  const formatPrice = (price: number) => {
    if (price >= 10000000) {
      return `${(price / 10000000).toFixed(2)} Cr`;
    } else if (price >= 100000) {
      return `${(price / 100000).toFixed(2)} L`;
    }
    return `${price.toLocaleString('en-IN')}`;
  };

  const getStatusColor = (status: UnitStatus) => {
    switch (status) {
      case 'AVAILABLE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'HOLD':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'BOOKED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'SOLD':
        return 'bg-secondary-100 text-secondary-800 border-secondary-200';
      default:
        return 'bg-secondary-100 text-secondary-800 border-secondary-200';
    }
  };

  const getConfigurations = (units: Unit[]) => {
    const configs = [...new Set(units.map((u) => u.configuration))];
    return configs.sort();
  };

  const filteredUnits = property?.units?.filter(
    (unit) => selectedConfig === 'all' || unit.configuration === selectedConfig
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-secondary-600">Loading property...</p>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">Failed to load property</h3>
        <p className="text-secondary-600 mb-4">{error || 'Property not found'}</p>
        <Button onClick={() => navigate('/properties')}>Back to Properties</Button>
      </div>
    );
  }

  const configurations = property.units ? getConfigurations(property.units) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/properties')}
          className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-secondary-900">{property.name}</h1>
          {property.developer_name && (
            <p className="text-secondary-600">by {property.developer_name}</p>
          )}
        </div>
      </div>

      {/* Property Overview */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Image Placeholder */}
          <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
            <div className="h-64 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
              <svg className="w-24 h-24 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>

          {/* Description */}
          {property.description && (
            <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-secondary-900 mb-3">About</h2>
              <p className="text-secondary-600 whitespace-pre-wrap">{property.description}</p>
            </div>
          )}

          {/* Amenities */}
          {property.amenities && property.amenities.length > 0 && (
            <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-secondary-900 mb-3">Amenities</h2>
              <div className="flex flex-wrap gap-2">
                {property.amenities.map((amenity, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full text-sm"
                  >
                    {amenity}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-secondary-900 mb-4">Quick Stats</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-secondary-600">Location</span>
                <span className="font-medium text-secondary-900">
                  {property.location}
                  {property.city && `, ${property.city}`}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-secondary-600">Total Units</span>
                <span className="font-medium text-secondary-900">{property.total_units}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-secondary-600">Available</span>
                <span className="font-medium text-green-600">{property.available_units}</span>
              </div>
              {property.price_range_min && property.price_range_max && (
                <div className="flex items-center justify-between">
                  <span className="text-secondary-600">Price Range</span>
                  <span className="font-medium text-secondary-900">
                    {formatPrice(property.price_range_min)} - {formatPrice(property.price_range_max)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-secondary-900 mb-4">Interested?</h2>
            <p className="text-secondary-600 text-sm mb-4">
              Schedule a site visit for your leads at this property.
            </p>
            <Button
              className="w-full"
              onClick={() => navigate('/visits/schedule')}
            >
              Schedule Visit
            </Button>
          </div>
        </div>
      </div>

      {/* Units Section */}
      {property.units && property.units.length > 0 && (
        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm">
          <div className="p-6 border-b border-secondary-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-secondary-900">Available Units</h2>
              {configurations.length > 1 && (
                <select
                  value={selectedConfig}
                  onChange={(e) => setSelectedConfig(e.target.value)}
                  className="px-3 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Configurations</option>
                  {configurations.map((config) => (
                    <option key={config} value={config}>
                      {config}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Configuration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Floor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Area
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {filteredUnits?.map((unit) => (
                  <tr key={unit.id} className="hover:bg-secondary-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-secondary-900">{unit.unit_number}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-secondary-600">
                      {unit.configuration}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-secondary-600">
                      Floor {unit.floor}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-secondary-600">
                      {unit.area_sqft.toLocaleString()} sq.ft
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-secondary-900">
                      {formatPrice(unit.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(unit.status)}`}>
                        {unit.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetail;
